
import React, { useState } from 'react';
import { 
  Plus, MoreVertical, Calendar, Construction, 
  HeartPulse, ShoppingCart, Zap, Lock, Unlock, Cpu, 
  Mail, Phone, UserCheck, Settings, Trash2, ExternalLink, X, Save, ShieldCheck,
  DollarSign
} from 'lucide-react';
import { Company, AppVertical } from './types';
import { Badge } from './SharedComponents';

interface CompaniesManagementProps {
  companies: Company[];
  onNewCompany: () => void;
  onToggleNfse: (id: string) => void;
  onToggleBlock: (id: string) => void;
  onDeleteCompany: (id: string) => void;
  onManageLicense: (id: string, updates: Partial<Company>) => void;
  onImpersonate: (id: string) => void;
}

export const CompaniesManagement = ({ 
  companies, 
  onNewCompany, 
  onToggleNfse, 
  onToggleBlock, 
  onDeleteCompany, 
  onManageLicense, 
  onImpersonate 
}: CompaniesManagementProps) => {
  const [activeMenu, setActiveMenu] = useState<{id: string, x: number, y: number} | null>(null);
  const [licenseModalCompany, setLicenseModalCompany] = useState<Company | null>(null);

  const rowPalettes = [
    { border: 'border-l-primary', bg: 'hover:bg-primary/[0.03]', iconBg: 'bg-primary/10', iconText: 'text-primary' },
    { border: 'border-l-green-500', bg: 'hover:bg-green-500/[0.03]', iconBg: 'bg-green-50', iconText: 'text-green-600' },
    { border: 'border-l-purple-500', bg: 'hover:bg-purple-500/[0.03]', iconBg: 'bg-purple-50', iconText: 'text-purple-600' },
    { border: 'border-l-amber-500', bg: 'hover:bg-amber-500/[0.03]', iconBg: 'bg-amber-50', iconText: 'text-amber-600' },
  ];

  const getVerticalIcon = (v: AppVertical) => {
    switch(v) {
      case AppVertical.CONSTRUCTION: return <Construction size={14} />;
      case AppVertical.MEDICAL: return <HeartPulse size={14} />;
      case AppVertical.RETAIL: return <ShoppingCart size={14} />;
      default: return <Calendar size={14} />;
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 min-h-screen text-secondary bg-[#fcfdfe] p-10">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 px-2">
        <div>
          <h1 className="text-4xl font-black uppercase tracking-tighter text-secondary leading-none italic">Locadoras SaaS</h1>
          <div className="text-primary font-black uppercase text-[10px] tracking-[0.4em] mt-3 italic opacity-80 flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse"></div>
            Base de Dados de Unidades Licenciadas
          </div>
        </div>
        <button 
          onClick={(e) => { e.preventDefault(); onNewCompany(); }} 
          className="w-full sm:w-auto bg-primary text-white px-10 py-5 rounded-2xl font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-2 hover:brightness-110 shadow-2xl shadow-primary/30 transition-all active:scale-95"
        >
          <Plus size={20} strokeWidth={3} /> Nova Unidade
        </button>
      </div>

      <div className="overflow-x-auto scrollbar-hide pb-10">
        <table className="w-full text-left border-separate border-spacing-y-4 min-w-[1200px]">
          <thead>
            <tr className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
              <th className="px-8 pb-2">Empresa / Tenant</th>
              <th className="pb-2">Endereço de Operação</th>
              <th className="pb-2">Gestor / Contatos</th>
              <th className="pb-2 text-center">IA Usage</th>
              <th className="pb-2 text-center">Status Fiscal</th>
              <th className="px-8 pb-2 text-right">Ação</th>
            </tr>
          </thead>
          <tbody>
            {companies.map((c, index) => {
              const palette = rowPalettes[index % rowPalettes.length];
              
              return (
                <tr key={c.id} className={`group transition-all ${palette.bg}`}>
                  <td className={`px-8 py-6 rounded-l-[32px] border-l-4 ${palette.border} bg-white shadow-sm`}>
                    <div className="flex items-center gap-5">
                      <div className={`w-14 h-14 rounded-2xl ${palette.iconBg} flex items-center justify-center font-black text-2xl shadow-inner transition-transform group-hover:scale-110 ${c.blocked ? 'text-red-500' : palette.iconText}`}>
                        {c.name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-black text-secondary uppercase tracking-tight text-base mb-0.5">{c.name}</p>
                        <p className="text-[9px] text-gray-300 font-bold uppercase tracking-widest">CNPJ: {c.cnpj}</p>
                        <div className="mt-2 flex items-center gap-2">
                           <Badge className="bg-secondary text-white border-none text-[8px] px-2">{c.plan}</Badge>
                           <Badge className="bg-gray-100 text-gray-500 border-none text-[8px] px-2 flex items-center gap-1">
                              {getVerticalIcon(c.vertical)} {c.vertical}
                           </Badge>
                        </div>
                      </div>
                    </div>
                  </td>
                  
                  <td className="py-6 bg-white shadow-sm max-w-[280px]">
                    <div className="space-y-1">
                       <p className="text-[11px] font-black text-secondary uppercase leading-tight truncate">{c.address}</p>
                       <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight italic truncate">{c.neighborhood} • {c.city} - {c.state}</p>
                    </div>
                  </td>

                  <td className="py-6 bg-white shadow-sm min-w-[220px]">
                    <div className="space-y-2">
                       <div className="flex items-center gap-2">
                          <UserCheck size={14} className="text-primary" />
                          <span className="text-[11px] font-black text-secondary uppercase">{c.managerName}</span>
                       </div>
                       <div className="flex flex-col gap-1">
                          <button className="flex items-center gap-2 text-[10px] font-bold text-green-600 hover:underline">
                             <Phone size={12} /> {c.whatsapp}
                          </button>
                          <button className="flex items-center gap-2 text-[10px] font-bold text-gray-400 hover:text-primary transition-colors truncate max-w-[180px]">
                             <Mail size={12} /> {c.contactEmail}
                          </button>
                       </div>
                    </div>
                  </td>

                  <td className="py-6 text-center bg-white shadow-sm">
                    <div className="inline-flex flex-col items-center gap-1.5">
                       <div className="flex items-center gap-2">
                          <Cpu size={14} className={palette.iconText} />
                          <span className={`text-xs font-black ${palette.iconText}`}>{c.iaUsageCount}</span>
                       </div>
                       <div className="w-16 bg-gray-100 h-1 rounded-full overflow-hidden">
                          <div className={`${palette.iconText.replace('text', 'bg')} h-full`} style={{ width: `${Math.min((c.iaUsageCount/1000)*100, 100)}%` }}></div>
                       </div>
                    </div>
                  </td>

                  <td className="py-6 text-center bg-white shadow-sm">
                    <button 
                      onClick={() => onToggleNfse(c.id)}
                      className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase flex items-center gap-2 mx-auto transition-all border-2 ${
                        c.nfseEnabled 
                          ? 'bg-primary border-primary text-white shadow-lg shadow-primary/20' 
                          : 'bg-white border-gray-100 text-gray-400 hover:border-primary/30 hover:text-primary'
                      }`}
                    >
                      <Zap size={10} fill={c.nfseEnabled ? 'currentColor' : 'none'} />
                      {c.nfseEnabled ? 'NFS-e On' : 'Fisc. Off'}
                    </button>
                  </td>

                  <td className="px-8 py-6 text-right rounded-r-[32px] bg-white shadow-sm overflow-visible">
                    <div className="flex items-center justify-end gap-2 relative overflow-visible">
                       <button 
                        onClick={() => onToggleBlock(c.id)}
                        className={`p-3 rounded-xl transition-all shadow-sm ${
                          c.blocked 
                            ? 'bg-red-50 text-red-500 hover:bg-red-100 ring-2 ring-red-500 animate-in pulse-slow' 
                            : 'bg-gray-50 text-gray-300 hover:text-red-500 hover:bg-red-50'
                        }`}
                        title={c.blocked ? 'Desbloquear Inquilino' : 'Bloquear Access'}
                      >
                        {c.blocked ? <Lock size={18} /> : <Unlock size={18} />}
                      </button>
                      
                      <div className="relative">
                        <button 
                          onClick={(e) => { 
                            e.stopPropagation();
                            const rect = e.currentTarget.getBoundingClientRect();
                            // Menu posicionado abaixo do botão
                            setActiveMenu(activeMenu?.id === c.id ? null : { id: c.id, x: rect.left - 200, y: rect.bottom + 10 }); 
                          }}
                          className={`p-3 transition-all rounded-xl border border-transparent ${activeMenu?.id === c.id ? 'bg-secondary text-white shadow-lg' : 'bg-gray-50 text-gray-300 hover:text-primary hover:bg-white hover:border-gray-100 active:scale-90'}`}
                        >
                          <MoreVertical size={20} />
                        </button>
                        
                        {activeMenu?.id === c.id && (
                          <>
                            <div className="fixed inset-0 z-[1000]" onClick={() => setActiveMenu(null)}></div>
                            <div 
                              className="fixed w-64 bg-white border border-gray-100 rounded-3xl shadow-[0_40px_100px_rgba(0,0,0,0.3)] z-[2000] overflow-hidden animate-in fade-in zoom-in-95 p-2 text-left"
                              style={{ top: activeMenu.y, left: activeMenu.x }}
                            >
                               <div className="px-4 py-3 border-b border-gray-50 mb-1 bg-gray-50/30">
                                  <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Ações Master</p>
                                  <p className="text-[11px] font-black text-secondary truncate uppercase italic">{c.name}</p>
                               </div>
                               <button 
                                 type="button"
                                 onClick={(e) => { e.stopPropagation(); setLicenseModalCompany(c); setActiveMenu(null); }}
                                 className="w-full text-left px-4 py-3.5 text-[11px] font-black uppercase text-secondary hover:bg-primary/5 hover:text-primary rounded-2xl transition-all flex items-center gap-3 group"
                               >
                                  <Settings size={18} className="group-hover:rotate-45 transition-transform" /> Gerenciar Licença
                               </button>
                               <button 
                                 type="button"
                                 onClick={(e) => { e.stopPropagation(); onImpersonate(c.id); setActiveMenu(null); }}
                                 className="w-full text-left px-4 py-3.5 text-[11px] font-black uppercase text-secondary hover:bg-primary/5 hover:text-primary rounded-2xl transition-all flex items-center gap-3"
                               >
                                  <ExternalLink size={18} /> Ver Dash Operacional
                               </button>
                               <div className="h-[1px] bg-gray-50 mx-2 my-2"></div>
                               <button 
                                 type="button"
                                 onClick={(e) => { e.stopPropagation(); onDeleteCompany(c.id); setActiveMenu(null); }}
                                 className="w-full text-left px-4 py-3.5 text-[11px] font-black uppercase text-red-500 hover:bg-red-50 rounded-2xl transition-all flex items-center gap-3"
                               >
                                  <Trash2 size={18} /> Excluir Unidade
                               </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal de Gestão de Licença */}
      {licenseModalCompany && (
        <LicenseManagementModal 
          company={licenseModalCompany} 
          onClose={() => setLicenseModalCompany(null)} 
          onSave={(updates) => { 
            onManageLicense(licenseModalCompany.id, updates); 
            setLicenseModalCompany(null); 
          }} 
        />
      )}
    </div>
  );
};

const LicenseManagementModal = ({ company, onClose, onSave }: { company: Company, onClose: () => void, onSave: (u: Partial<Company>) => void }) => {
  const [formData, setFormData] = useState({
    plan: company.plan,
    mrr: company.mrr,
    licenseExpiry: company.licenseExpiry || '2025-12-31',
    nfseEnabled: company.nfseEnabled
  });

  return (
    <div 
      className="fixed inset-0 bg-secondary/90 backdrop-blur-md z-[3000] flex items-center justify-center p-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
       <div className="bg-white rounded-[48px] w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in-95">
          <div className="p-8 bg-[#1e293b] text-white flex justify-between items-center relative overflow-hidden">
             <div className="absolute top-0 right-0 p-8 opacity-5 rotate-12 pointer-events-none"><ShieldCheck size={120} /></div>
             <div className="relative z-10">
                <h2 className="text-2xl font-black uppercase tracking-tight italic">Gestão de Licença</h2>
                <p className="text-[10px] font-bold text-primary uppercase tracking-[0.4em] mt-3">Unidade: {company.name}</p>
             </div>
             <button 
                type="button"
                onClick={(e) => { e.stopPropagation(); onClose(); }} 
                className="p-3 bg-white/10 hover:bg-white/20 rounded-2xl transition-all active:scale-90"
             >
                <X size={28} />
             </button>
          </div>
          
          <div className="p-10 space-y-8 bg-[#fcfdfe]">
             <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Plano Contratado</label>
                <div className="grid grid-cols-3 gap-3">
                   {['START', 'PRO', 'ENTERPRISE'].map(p => (
                     <button 
                       key={p} 
                       type="button"
                       onClick={() => setFormData({...formData, plan: p as any})}
                       className={`py-4 rounded-2xl font-black text-[10px] uppercase border-2 transition-all ${formData.plan === p ? 'bg-primary border-primary text-white shadow-lg' : 'bg-white border-gray-100 text-gray-400 hover:border-primary/20'}`}
                     >
                       {p}
                     </button>
                   ))}
                </div>
             </div>

             <div className="grid grid-cols-2 gap-6">
                <div className="space-y-1.5">
                   <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Valor MRR (R$)</label>
                   <div className="relative">
                      <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-primary" size={16} />
                      <input 
                        type="number"
                        className="w-full bg-white border border-gray-100 p-4 pl-10 rounded-2xl outline-none font-bold text-secondary" 
                        value={formData.mrr}
                        onChange={e => setFormData({...formData, mrr: parseFloat(e.target.value)})}
                      />
                   </div>
                </div>
                <div className="space-y-1.5">
                   <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Expiração</label>
                   <input 
                     type="date"
                     className="w-full bg-white border border-gray-100 p-4 rounded-2xl outline-none font-bold text-secondary" 
                     value={formData.licenseExpiry}
                     onChange={e => setFormData({...formData, licenseExpiry: e.target.value})}
                   />
                </div>
             </div>

             <div className="flex items-center justify-between p-6 bg-slate-50 rounded-3xl border border-slate-100">
                <div className="flex items-center gap-4">
                   <div className={`p-3 rounded-xl ${formData.nfseEnabled ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400'}`}>
                      <Zap size={20} fill={formData.nfseEnabled ? 'currentColor' : 'none'} />
                   </div>
                   <div>
                      <p className="text-xs font-black uppercase text-secondary">Módulo NFS-e Ativo</p>
                      <p className="text-[8px] font-bold text-gray-400 uppercase">Integração Direta com Prefeitura</p>
                   </div>
                </div>
                <button 
                   type="button"
                   onClick={() => setFormData({...formData, nfseEnabled: !formData.nfseEnabled})}
                   className={`w-14 h-8 rounded-full p-1 transition-all flex items-center ${formData.nfseEnabled ? 'bg-primary justify-end' : 'bg-gray-200 justify-start'}`}
                >
                   <div className="w-6 h-6 bg-white rounded-full shadow-md"></div>
                </button>
             </div>

             <div className="pt-4">
                <button 
                  type="button"
                  onClick={() => onSave(formData)}
                  className="w-full py-6 bg-secondary text-white rounded-[32px] font-black uppercase text-xs tracking-[0.2em] shadow-2xl flex items-center justify-center gap-3 active:scale-95 hover:bg-black transition-all"
                >
                   <Save size={20} /> Efetivar Alterações
                </button>
             </div>
          </div>
       </div>
    </div>
  );
};
